<template>
    <HamletStaticTable
        :columns="columns"
        :items="localValue"
        :striped="false"
        :bordered="true"
        empty-mode="message"
        card-class="pt-0 mt-0"
    >
        <template #header-label>
            {{ $t('Component(ResearchReviewProcess):legislations') }}
        </template>

        <template #header-controls>
            <CommonButton
                v-if="canAddRow"
                variant="primary"
                size="sm"
                @click="addRow"
            >
                {{ $t('Component(Button):add') }}
            </CommonButton>
        </template>

        <template #cell-legislation="{ item }">
            <v-select
                :value="selectedLegislationOption(item)"
                :options="availableOptionsForRow(item)"
                label="title"
                :clearable="false"
                :append-to-body="true"
                @input="onLegislationChange(item, $event)"
            />
        </template>

        <template #cell-status="{ item }">
            <v-select
                v-model="item.status"
                :options="statusOptions"
                label="label"
                :reduce="option => option.id"
                :clearable="false"
                :append-to-body="true"
            />
        </template>

        <template #cell-evidence="{ item }">
            <TextEllipsis
                :value="item.evidence || item.reference"
                :max-length="30"
            />
        </template>

        <template #cell-actions="{ item }">
            <CommonButton
                variant="danger"
                size="sm"
                class="btn-icon"
                @click="removeRow(item)"
            >
                <feather-icon icon="MinusIcon" size="14"/>
            </CommonButton>
        </template>
    </HamletStaticTable>
</template>

<script>
import {HamletStaticTable, Button as CommonButton, TextEllipsis} from '@core/components/common'
import {LegislationApi} from '@core/api'
import vSelect from 'vue-select'

function clone(value) {
    return JSON.parse(JSON.stringify(value || []))
}

function isEqual(a, b) {
    return JSON.stringify(a) === JSON.stringify(b)
}

function normalize(items) {
    return Array.isArray(items) ? clone(items) : []
}

export default {
    name: 'LegislationsSection',
    components: {
        CommonButton,
        HamletStaticTable,
        TextEllipsis,
        vSelect,
    },
    props: {
        value: {
            type: Array,
            default: () => [],
        },
    },
    data() {
        return {
            localValue: normalize(this.value),
            legislationOptions: [],
            columns: [
                {
                    key: 'legislation',
                    label: this.$t('Component(ResearchReviewProcess):legislation'),
                },
                {key: 'status', label: this.$t('Component(ComplianceOverview):column-status'), thClass: 'legislations-col-status', tdClass: 'legislations-col-status'},
                {
                    key: 'evidence',
                    label: this.$t('Component(ResearchReviewProcess):evidence'),
                },
                {key: 'actions', label: '', thClass: 'text-right', tdClass: 'text-right'},
            ],
        }
    },
    computed: {
        selectedLegislationKeys() {
            return this.localValue
                .map(row => row && row.key ? row.key : null)
                .filter(Boolean)
        },
        canAddRow() {
            return this.remainingLegislationOptions.length > 0
        },
        remainingLegislationOptions() {
            const selected = new Set(this.selectedLegislationKeys)
            return this.legislationOptions.filter(option => !selected.has(option.id))
        },
        statusOptions() {
            const options = [
                ...this.$store.getters['researchReview/legislationStatuses']
                    .map(status => ({
                        id: status,
                        label: this.$t('Component(ResearchReviewProcess):legislation-status-' + status),
                    })),
            ]

            const currentValues = this.localValue
                .map(row => row && row.status ? row.status : null)
                .filter(Boolean)

            currentValues.forEach((value) => {
                if (!options.find(option => option.id === value)) {
                    options.push({
                        id: value,
                        label: this.$t('Component(ResearchReviewProcess):legislation-status-' + value),
                    })
                }
            })

            return options
        },
    },
    watch: {
        value: {
            deep: true,
            handler(newValue) {
                const normalized = normalize(newValue)
                if (isEqual(normalized, this.localValue)) {
                    return
                }

                this.localValue = normalized
            },
        },
        localValue: {
            deep: true,
            handler(newValue) {
                const emitted = clone(newValue)
                const current = normalize(this.value)

                if (isEqual(emitted, current)) {
                    return
                }

                this.$emit('input', emitted)
            },
        },
    },
    created() {
        LegislationApi
            .all()
            .then((response) => {
                const rows = Array.isArray(response.data) ? response.data : []
                this.legislationOptions = rows
                    .filter(legislation => legislation && legislation.key)
                    .map(legislation => ({
                        id: legislation.key,
                        title: legislation.title || legislation.key,
                    }))
            })
            .catch(() => {
                this.legislationOptions = []
            })
    },
    methods: {
        nextTempId() {
            return 'tmp-legislation-' + Date.now() + '-' + Math.random().toString(16).slice(2)
        },
        addRow() {
            if (!this.canAddRow) {
                return
            }

            this.localValue.push({
                id: this.nextTempId(),
                key: null,
                name: null,
                status: null,
                evidence: null,
            })
        },
        removeRow(item) {
            const index = this.localValue.indexOf(item)
            if (index === -1) {
                return
            }

            this.localValue.splice(index, 1)
        },
        availableOptionsForRow(item) {
            const currentKey = item && item.key ? item.key : null
            const selected = new Set(this.selectedLegislationKeys.filter(key => key !== currentKey))

            const options = this.legislationOptions.filter(option => !selected.has(option.id))
            if (currentKey && !options.find(option => option.id === currentKey)) {
                options.push({
                    id: currentKey,
                    title: item && item.name ? item.name : currentKey,
                })
            }

            return options
        },
        selectedLegislationOption(item) {
            if (!item || !item.key) {
                return null
            }

            const found = this.legislationOptions.find(option => option.id === item.key)
            if (found) {
                return found
            }

            return {
                id: item.key,
                title: item.name || item.key,
            }
        },
        onLegislationChange(item, option) {
            if (!item) {
                return
            }

            item.key = option.id
            item.name = option.title
        },
    },
}
</script>

<style scoped>
::v-deep .legislations-col-status {
    width: 220px;
    min-width: 220px;
    max-width: 220px;
}
</style>
